"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileText, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface SpecItem {
  label: L6;
  value: L6;
}
const technicalSpecs: SpecItem[] = [{
  label: {
    en: "Standard Weave Styles",
    zh: "标准编织形式",
    es: "Tipos de tejido estándar",
    de: "Standard-Webarten",
    fr: "Types de tissage standard",
    pt: "Tipos de trama padrão"
  },
  value: {
    en: "Plain, Twilled & Dutch",
    zh: "平纹、斜纹与密纹编织",
    es: "Tejidos liso, sarga y Dutch",
    de: "Glatte, Köper- und Tressengewebe",
    fr: "Tissages uni, sergé et hollandais",
    pt: "Tramas lisa, sarja e holandesa"
  }
}, {
  label: {
    en: "Filtration Rating",
    zh: "过滤精度范围",
    es: "Rango de filtración",
    de: "Filterbereich",
    fr: "Plage de filtration",
    pt: "Faixa de filtração"
  },
  value: {
    en: "1 µm to 1000 µm",
    zh: "1 µm 至 1000 µm",
    es: "1 µm a 1000 µm",
    de: "1 µm bis 1000 µm",
    fr: "1 µm à 1000 µm",
    pt: "1 µm a 1000 µm"
  }
}, {
  label: {
    en: "Standard Alloys",
    zh: "标准合金材质",
    es: "Aleaciones estándar",
    de: "Standardlegierungen",
    fr: "Alliages standard",
    pt: "Ligas padrão"
  },
  value: {
    en: "AISI 304, 316L, 904L, Nickel Alloys",
    zh: "AISI 304、316L、904L及镍基合金",
    es: "AISI 304, 316L, 904L y aleaciones de níquel",
    de: "AISI 304, 316L, 904L, Nickellegierungen",
    fr: "AISI 304, 316L, 904L, alliages de nickel",
    pt: "AISI 304, 316L, 904L, ligas de níquel"
  }
}, {
  label: {
    en: "Custom Fabrication",
    zh: "定制加工工艺",
    es: "Fabricación a medida",
    de: "Kundenspezifische Fertigung",
    fr: "Fabrication sur mesure",
    pt: "Fabricação sob medida"
  },
  value: {
    en: "Coils, Discs, Cartridges & Cylinders",
    zh: "分条网带、冲压滤片、滤芯与焊接滤筒",
    es: "Bobinas, discos, cartuchos y cilindros",
    de: "Bänder, Ronden, Kerzen & Zylinder",
    fr: "Bandes, disques, cartouches et cylindres",
    pt: "Bobinas, discos, cartuchos e cilindros"
  }
}];
const copy = {
  eyebrow: {
    en: "Liquid Process Filtration",
    zh: "液体工艺过滤",
    es: "Filtración de procesos líquidos",
    de: "Flüssigkeitsprozessfiltration",
    fr: "Filtration de procédés liquides",
    pt: "Filtração de processos líquidos"
  },
  standardTag: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  h1: {
    en: "Precision wire mesh for critical liquid filtration.",
    zh: "关键液体过滤与分离的高精度不锈钢丝网",
    es: "Malla de alambre de precisión para filtración crítica de líquidos.",
    de: "Präzisionsdrahtgewebe für anspruchsvolle Flüssigkeitsfiltration.",
    fr: "Toile métallique de précision pour la filtration critique des liquides.",
    pt: "Malha de arame de precision para filtração crítica de líquidos."
  },
  lead: {
    en: "Engineered from austenitic stainless steels and specialty nickel-chromium alloys. Built to withstand high differential pressures, thermal shocks, and aggressive process fluids without media migration.",
    zh: "采用奥氏体不锈钢及特种镍铬合金精密制造。专为承受高压差、热冲击和苛刻工艺流体而设计，杜绝滤料脱落与介质迁移。",
    es: "Fabricada en aceros inoxidables austeníticos y aleaciones especiales de níquel-cromo. Diseñada para soportar altas presiones diferenciales, choques térmicos y fluidos agresivos sin desprendimiento de fibras.",
    de: "Hergestellt aus austenitischen Edelstählen und Nickel-Chrom-Sonderlegierungen. Entwickelt für hohe Differenzdrücke, Temperaturschocks und aggressive Prozessmedien ohne Faserabgabe.",
    fr: "Fabriquée en aciers inoxydables austénitiques et alliages spéciaux de nickel-chrome. Conçue pour résister aux pressions différentielles élevées, chocs thermiques et fluides agressifs sans relargage de fibres.",
    pt: "Fabricada em aços inoxidáveis austeníticos e ligas especiais de níquel-cromo. Desenvolvida para resistir a altas pressões diferenciais, choques térmicos e fluidos agressivos sem desprendimento de fibras."
  },
  relatedCta: {
    en: "Water & liquid filtration",
    zh: "水与液体过滤概览",
    es: "Filtración de agua y líquidos",
    de: "Wasser- & Flüssigkeitsfiltration",
    fr: "Filtration de l'eau et des liquides",
    pt: "Filtração de água e líquidos"
  },
  imgAlt: {
    en: "Microscopic view of stainless steel twilled dutch weave liquid filtration mesh",
    zh: "不锈钢斜纹密纹液体过滤网微观结构图",
    es: "Vista microscópica de malla de filtración de líquidos en tejido Dutch cruzado de acero inoxidable",
    de: "Mikroskopische Ansicht von Edelstahl-Köpertressengewebe für die Flüssigkeitsfiltration",
    fr: "Vue microscopique de toile de filtration liquide en tissage hollandais sergé en acier inoxydable",
    pt: "Visualização microscópica de malha de filtração de líquidos em trama holandesa sarja de aço inoxidável"
  },
  cardBadge: {
    en: "Zero Media Migration",
    zh: "零介质脱落 · 纯金属结构",
    es: "Cero migración de medio",
    de: "Keine Partikelabgabe",
    fr: "Zéro relargage de matière",
    pt: "Zero migração de meio"
  },
  cardFooterNote: {
    en: "Ultrasonic & backwashable metallic media",
    zh: "支持超声波清洗与自动反冲洗再生",
    es: "Medio metálico lavable por ultrasonidos y retrolavable",
    de: "Ultraschall- und rückspülbares Metallgewebe",
    fr: "Média métallique lavable aux ultrasons et rétrolavable",
    pt: "Meio metálico lavável por ultrassom e retrolavável"
  },
  cardFooterLink: {
    en: "OEM Custom",
    zh: "OEM定制",
    es: "OEM personalizado",
    de: "OEM-Kundenspezifisch",
    fr: "OEM sur mesure",
    pt: "OEM personalizado"
  }
};
export default function LiquidHero() {
  const {
    lang
  } = useLanguage();
  return <section id="liquidfiltrationhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column: Badges, Headline, Lead & CTAs (lg:col-span-7) */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <Reveal>
              {/* Badges row: exactly two outline badges */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.eyebrow)}</span>
                </Badge>
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-2.5 py-1 font-mono mf-caption text-foreground">
                  <ShieldCheck className="size-3.5 text-primary inline" aria-hidden="true" />
                  <span>{pick(lang, copy.standardTag)}</span>
                </Badge>
              </div>

              {/* Page h1 */}
              <h1 className="font-default text-balance mf-h1 text-foreground">
                {pick(lang, copy.h1)}
              </h1>

              {/* Lead paragraph (2-3 sentences) */}
              <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
                {pick(lang, copy.lead)}
              </p>

              {/* CTAs */}
              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>

                <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                  <Link to="/WaterLiquidFiltrationPage" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, copy.relatedCta)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: ONE bordered Card (lg:col-span-5) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={60}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
                <div className="mf-card-media relative mf-ratio-hero w-full overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-fine-stainless-steel-711235.png" alt={pick(lang, copy.imgAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-2.5 py-1 rounded-md mf-caption font-semibold text-foreground flex items-center gap-1.5">
                    <FileText className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{pick(lang, copy.cardBadge)}</span>
                  </div>
                </div>

                <CardContent className="p-4 bg-card">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                      <span>{pick(lang, copy.cardFooterNote)}</span>
                    </div>
                    <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 inline-flex items-center gap-1 shrink-0">
                      <span>{pick(lang, copy.cardFooterLink)}</span>
                      <ArrowRight className="size-3" aria-hidden="true" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* 4-Item Spec Strip under the grid */}
        <div className="mf-spec-strip">
          {technicalSpecs.map((spec, i) => <div key={i} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </span>
              <span className="mf-spec-strip__value font-mono">
                {pick(lang, spec.value)}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}